import React from "react";

import * as Styled from "./styles";
import { Button, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useClinicalEvaluation } from "shared/providers/ClinicalEvaluation";
import { sendSchedule } from "requests/SendSchedule";
import { ISendSchedule, IOdont, INextAppointment } from "shared/types/SendSchedule";
import toast from "react-hot-toast";
import { useAuth } from "shared/providers/auth";
import SignatureCanvas from "react-signature-canvas";
import { ROUTER } from "shared/constants/router";
import { IoReceiptOutline, IoCloseCircleOutline } from "react-icons/io5";

const ClinicalEvaluationRecord: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    setStep,
    complaint,
    treatmentEvolution,
    patientObj,
    clearClinicalEvaluationData,
  } = useClinicalEvaluation();

  const [isBtnFixedVisible, setIsBtnFixedVisible] =
    React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showBudgetModal, setShowBudgetModal] = React.useState<boolean>(false);
  
  const [signatureDoctor, setSignatureDoctor] = React.useState<any>();
  const [signaturePatient, setSignaturePatient] = React.useState<any>();

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);
  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);

  const saveSignaturePatient = () => {
    if (sigCanvasPatient.current) {
      const dataURL = sigCanvasPatient.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setSignaturePatient(dataURL);
    }
  };

  const saveSignatureDentist = () => {
    if (sigCanvasDentist.current) {
      const dataURL = sigCanvasDentist.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setSignatureDoctor(dataURL);
    }
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentist = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const finalize = () => {
    if (!signaturePatient) {
      toast.error("Por favor, assine como paciente.");
      return;
    }

    if (!signatureDoctor) {
      toast.error("Por favor, assine como dentista.");
      return;
    }

    if (Number(patientObj.dentista_orcamento) === 1) {
      setShowBudgetModal(true);
    } else {
      sendConsultDirect();
    }
  };

  const hasActiveOrto = !!patientObj?.budget;

  const buildDocData = () => {
    const procedures: any[] = [
      { question: "Avaliação Clínica - Queixa Principal", description: complaint },
    ];
    let observations = `AVALIAÇÃO CLÍNICA\n\nQueixa Principal:\n${complaint}`;

    if (hasActiveOrto && treatmentEvolution) {
      procedures.push({ question: "Avaliação Clínica - Evolução do Tratamento", description: treatmentEvolution });
      observations += `\n\nEvolução do Tratamento:\n${treatmentEvolution}`;
    }

    return { procedures, observations, evaluation: true };
  };

  const handleGenerateBudget = () => {
    const docData = buildDocData();

    const payload: ISendSchedule = {
      id: user.data.id,
      idPac: patientObj.pac_id,
      idRecord: patientObj.record_id || 0,
      idSchedule: patientObj.id,
      idBudget: patientObj.budget || 0,
      budget: 0,
      nextAppointment: [] as INextAppointment[],
      odont: [] as IOdont[],
      schedules: [],
      doc: docData,
      signature: {
        pac:
          signaturePatient &&
          signaturePatient.replace(/^data:image\/[a-z]+;base64,/, ""),
        dent:
          signatureDoctor &&
          signatureDoctor.replace(/^data:image\/[a-z]+;base64,/, ""),
      },
      hasFuture: patientObj.future?.scheduling === true ? 1 : 0,
    };

    // Salva os dados da avaliação para finalizar após o orçamento
    const avaliacaoData = {
      type: 'schedule',
      payload: payload,
    };
    localStorage.setItem('pendingAvaliation', JSON.stringify(avaliacaoData));
    
    // Navega para o orçamento clínico
    setShowBudgetModal(false);
    navigate(ROUTER.CLINICAL_BUDGET);
  };

  const handleSkipBudget = () => {
    // Finaliza sem gerar orçamento (já validou assinaturas no finalize)
    sendConsultDirect();
  };

  const sendConsultDirect = async () => {
    setLoading(true);

    try {
      const docData = buildDocData();

      const payload: ISendSchedule = {
        id: user.data.id,
        idPac: patientObj.pac_id,
        idRecord: patientObj.record_id || 0,
        idSchedule: patientObj.id,
        idBudget: patientObj.budget || 0,
        budget: 0,
        nextAppointment: [] as INextAppointment[],
        odont: [] as IOdont[],
        schedules: [],
        doc: docData,
        signature: {
          pac:
            signaturePatient &&
            signaturePatient.replace(/^data:image\/[a-z]+;base64,/, ""),
          dent:
            signatureDoctor &&
            signatureDoctor.replace(/^data:image\/[a-z]+;base64,/, ""),
        },
        hasFuture: patientObj.future?.scheduling === true ? 1 : 0,
      };

      const { status, data } = await sendSchedule(payload);

      if ([200, 201].includes(status)) {
        toast.success(data.message || "Avaliação clínica registrada com sucesso!");
        clearClinicalEvaluationData(); // Limpar dados da avaliação
        localStorage.removeItem("personAttendance");
        localStorage.removeItem("pendingAvaliation");
        navigate("/home");
      } else {
        toast.error(data?.message || "Erro ao registrar a avaliação clínica.");
      }
    } catch (error) {
      console.error("Erro ao enviar avaliação:", error);
      toast.error("Ocorreu um erro ao registrar a avaliação clínica.");
    } finally {
      setLoading(false);
      setShowBudgetModal(false);
    }
  };

  const handlePrint = () => {
    setIsBtnFixedVisible(false);
    setTimeout(() => {
      window.print();
      setIsBtnFixedVisible(true);
    }, 0);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "flex-end",
          marginTop: "15px",
        }}
      >
        <Styled.BoxSave onClick={handlePrint} className="no-print">
          Imprimir
        </Styled.BoxSave>
      </Box>

      <Styled.Box className="printable">
        <Styled.BoxClinical>
          <Typography variant="h5">Avaliação Clínica</Typography>

          <Styled.Section>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Queixa Principal
            </Typography>
            <Typography sx={{ fontWeight: "500" }}>
              {complaint}
            </Typography>
          </Styled.Section>

          {hasActiveOrto && treatmentEvolution && (
            <Styled.Section>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Evolução do Tratamento
              </Typography>
              <Typography sx={{ fontWeight: "500" }}>
                {treatmentEvolution}
              </Typography>
            </Styled.Section>
          )}
        </Styled.BoxClinical>

        <Styled.BoxClinicalSignature className="boxValue">
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            Assinatura do Paciente{" "}
            <Button onClick={clearSignaturePatient} className="no-print">
              Limpar
            </Button>
          </Typography>
          <div className="boxSignature">
            <SignatureCanvas
              onEnd={saveSignaturePatient}
              ref={sigCanvasPatient}
              penColor="black"
              canvasProps={{ height: 200, className: "sigCanvas" }}
            />
          </div>
        </Styled.BoxClinicalSignature>

        <Styled.BoxClinicalSignature className="boxValue">
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            Assinatura do Dentista{" "}
            <Button onClick={clearSignatureDentist} className="no-print">
              Limpar
            </Button>
          </Typography>
          <div className="boxSignature">
            <SignatureCanvas
              onEnd={saveSignatureDentist}
              ref={sigCanvasDentist}
              penColor="black"
              canvasProps={{ height: 200, className: "sigCanvas" }}
            />
          </div>
        </Styled.BoxClinicalSignature>

        {isBtnFixedVisible ? (
          <Styled.BtnFixed>
            <Button fullWidth onClick={() => setStep(1)} variant="outlined">
              Voltar
            </Button>
            <Button fullWidth onClick={finalize} disabled={loading}>
              {loading ? "Finalizando..." : "Finalizar"}
            </Button>
          </Styled.BtnFixed>
        ) : (
          ""
        )}
      </Styled.Box>

      {/* Modal de Orçamento Clínico */}
      <Dialog
        open={showBudgetModal}
        onClose={() => !loading && setShowBudgetModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", pb: 1 }}>
          <IoReceiptOutline size={48} color="#16a34a" style={{ marginBottom: 8 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
            Orçamento Clínico
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" sx={{ color: "#64748b", mb: 2 }}>
            Deseja gerar um orçamento clínico para este paciente antes de finalizar a avaliação?
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Você poderá adicionar procedimentos clínicos como restaurações, limpezas, extrações, etc.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<IoReceiptOutline />}
            onClick={handleGenerateBudget}
            disabled={loading}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              fontSize: "1rem",
            }}
          >
            Sim, gerar orçamento clínico
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<IoCloseCircleOutline />}
            onClick={handleSkipBudget}
            disabled={loading}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.5,
              fontSize: "1rem",
              color: "#64748b",
              borderColor: "#e2e8f0",
            }}
          >
            {loading ? "Finalizando..." : "Não, apenas finalizar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { ClinicalEvaluationRecord };
